import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";

/**
 * AuthCallback — runs when the URL contains "#session_id=...".
 * Exchanges the session_id with our backend, which sets the httpOnly cookie,
 * then navigates to /admin.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/admin/login", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);
    const dest = window.location.pathname.startsWith("/docs") ? "/docs" : "/admin";
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        // Clear the fragment from the URL
        window.history.replaceState(null, "", window.location.pathname);
        navigate(dest, { replace: true, state: { user: data } });
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || "Sign-in failed";
        if (status === 403 && dest === "/docs") {
          navigate("/docs", { replace: true, state: { authError: "This Google account has no documentation access. Ask the IP owner to grant you a tier." } });
          return;
        }
        setError(status === 403 ? "Access denied. This Google account is not on the admin allowlist." : detail);
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-black text-white px-6" data-testid="auth-callback">
      <div className="max-w-md text-center">
        {error ? (
          <>
            <h1 className="ek-heading text-2xl mb-3">Sign-in failed</h1>
            <p className="text-zinc-300 mb-6">{error}</p>
            <button
              onClick={() => navigate("/admin/login", { replace: true })}
              className="ek-btn ek-btn-primary"
              data-testid="auth-callback-retry"
            >
              Try again
            </button>
          </>
        ) : (
          <>
            <div
              className="w-10 h-10 rounded-full border-2 border-white/30 border-t-white animate-spin mx-auto mb-5"
              aria-hidden="true"
            />
            <p className="text-zinc-300 text-sm tracking-[0.16em] uppercase">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
